//ICS-202
import { useState } from 'react';
import PratusSelect from 'components/DesignSystems/New/PratusSelect';
import IncidentObjectivesTable from '../../IncidentObjectivesTable';
import {
  useIncidentInfo,
  useUpdateIcsInfo,
} from '../../hooks/incidentInfoHooks';
import { useIcsFormPdfPreview } from 'components/IAP/context/IcsFormPdfPreviewContext';
import PratusTextArea from 'components/DesignSystems/New/PratusTextArea';
import PratusInput from 'components/DesignSystems/New/PratusInput';
import { useSearchParams } from 'react-router';
import { StylishNewCheckbox } from 'components/DesignSystems/New/StylishNewCheckbox';
import { useIncidentPermissions } from 'components/IAP/hooks/incidentHooks';

import ICSWidget from './ICSWidget';
import { ConditionalCollapsibleCard } from 'components/IAP/ConditionalCollapsibleCard';
import { getIncidentCardTitle } from '../USCGPage';
import dayjs from 'dayjs';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { SharedIcon } from 'components/SharedIcon/SharedIcon';
import {
  useFutureOperationalPeriod,
  useFutureOperationalPeriodNumber,
  useIncident,
} from 'components/IAP/hooks/incidentHooks';
import { useUpdateCurrentOperationalPeriod } from '@/hooks/incident/useIncidentManagement';

const communityLifelinesOptions = [
  { value: 'Safety and Security', label: 'Safety and Security' },
  { value: 'Transportation', label: 'Transportation' },
  { value: 'Hazardous Materials', label: 'Hazardous Materials' },
  { value: 'Health and Medical', label: 'Health and Medical' },
  { value: 'Energy', label: 'Energy' },
  { value: 'Communications', label: 'Communications' },
  { value: 'Food, Hydration, Shelter', label: 'Food, Hydration, Shelter' },
  { value: 'Water Systems', label: 'Water Systems' },
];

const IncidentObjectives: React.FC = () => {
  const updateCurrentPeriod = useUpdateCurrentOperationalPeriod();
  const { data: incidentInfo } = useIncidentInfo();
  const incidentMutation = useUpdateIcsInfo();
  const [searchParams] = useSearchParams();
  const phase = searchParams.get('phase');
  const mode = searchParams.get('mode');
  const sectionKey = `${phase}-${mode}-incident-objectives`;
  const { exportAsForm } = useIcsFormPdfPreview();
  const [startDateError, setStartDateError] = useState<string | null>(null);
  const [endDateError, setEndDateError] = useState<string | null>(null);

  const { data: incidentPermissions } = useIncidentPermissions();
  const canEditCards = incidentPermissions?.['incident:editCards'] ?? false;

  const { data: futurePeriod } = useFutureOperationalPeriod();
  const futurePeriodNumber = useFutureOperationalPeriodNumber();
  const { data: incidentData } = useIncident();

  const handleExport = () => {
    void exportAsForm({
      formname: 'ICS_202_CG',
      data: {},
      mapSketch: null,
      periodNumber: futurePeriodNumber,
    });
  };

  return (
    <ConditionalCollapsibleCard
      sectionKey={sectionKey}
      className="planning-widget"
      contentClassName="content mt-2"
      headerLeft={
        <ICSWidget.HeaderLeft
          iconName="description"
          iconColor="primary"
          title={getIncidentCardTitle('incidentObjectives')}
          subtitle="Priorities, Objectives, Critical Information Requirements (CIRs)"
        />
      }
      headerRight={
        <ICSWidget.HeaderRight
          reviewActionKey="objectives"
          icsRoles={['IC/UC', 'PSC']}
          handleExport={handleExport}
          exportText="Preview ICS-202"
        />
      }
    >
      <div>
        <div className="mb-4">
          <div className="mt-2 mb-2">Community Lifelines</div>
          <PratusSelect
            isMulti
            isDisabled={!canEditCards}
            value={incidentInfo?.communityLifelines?.map((lifeline) => ({
              value: lifeline,
              label: lifeline,
            }))}
            placeholder="Click to select community lifelines..."
            options={communityLifelinesOptions}
            onChange={(selectedOptions) => {
              const selected = selectedOptions
                ? (selectedOptions as { value: string; label: string }[]).map(
                    (option) => option.value
                  )
                : [];

              incidentMutation.mutate({
                ...incidentInfo,
                communityLifelines: selected,
              });
            }}
          />
        </div>
      </div>
      <PratusTextArea
        label="Incident Priorities"
        placeholder="Enter incident priorities..."
        disabled={!canEditCards}
        value={incidentInfo?.priorities}
        onSave={(value) => {
          incidentMutation.mutate({
            ...incidentInfo,
            priorities: value,
          });
        }}
      />
      <div className="mb-4">
        <IncidentObjectivesTable readOnly={!canEditCards} />
      </div>
      <PratusTextArea
        label="Operational Command Emphasis"
        placeholder="Enter operational period command emphasis..."
        disabled={!canEditCards}
        value={incidentInfo?.operationalPeriodCommandEmphasis || ''}
        onSave={(value) => {
          incidentMutation.mutate({
            ...incidentInfo,
            operationalPeriodCommandEmphasis: value,
          });
        }}
      />
      <div>
        <div className="mb-3">
          <StylishNewCheckbox
            label="Site Safety Plan Required"
            disabled={!canEditCards}
            checked={!!incidentInfo?.siteSafetyPlan}
            onClick={() =>
              incidentMutation.mutate({
                ...incidentInfo,
                siteSafetyPlan: !incidentInfo?.siteSafetyPlan,
              })
            }
          />
        </div>
        <PratusInput
          label="Site Safety Plan Located At"
          placeholder="Enter site safety plan location..."
          disabled={!canEditCards}
          value={incidentInfo?.siteSafetyPlanLocation || ''}
          onValueChange={(value) => {
            incidentMutation.mutate({
              ...incidentInfo,
              siteSafetyPlanLocation: value,
            });
          }}
        />
      </div>
      <PratusTextArea
        label="Critical Information Requirements (CIRs)"
        placeholder="Enter critical information requirements..."
        disabled={!canEditCards}
        value={incidentInfo?.criticalInformationRequirements || ''}
        onSave={(value) =>
          incidentMutation.mutate({
            ...incidentInfo,
            criticalInformationRequirements: value,
          })
        }
      />
      <PratusTextArea
        label="Key Decisions & Procedures"
        placeholder="Enter key decisions and procedures..."
        disabled={!canEditCards}
        value={incidentInfo?.keyDecisionsAndProcedures || ''}
        onSave={(value) =>
          incidentMutation.mutate({
            ...incidentInfo,
            keyDecisionsAndProcedures: value,
          })
        }
      />
      <div>
        {'Planned Start & Stop Time of Next Operational Period'}
        <span style={{ marginLeft: '0.5rem' }}>
          <OverlayTrigger
            placement="top"
            overlay={(props) => (
              <Tooltip {...props}>
                This value is for planning purposes only. The operational period
                is advanced within the Operations Briefing phase.
              </Tooltip>
            )}
          >
            <span
              className="d-inline-flex align-items-center"
              role="button"
              tabIndex={0}
            >
              <SharedIcon
                iconName="info"
                size="sm"
                color="primary"
                style={{ cursor: 'default' }}
              />
            </span>
          </OverlayTrigger>
        </span>
        <div
          style={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            marginTop: '0.5rem',
            gap: '1rem',
          }}
        >
          <PratusInput
            label="Planned Start Date:"
            placeholder="Enter planned start date..."
            disabled={!canEditCards}
            value={
              futurePeriod?.initial_planned_start
                ? dayjs(futurePeriod.initial_planned_start).format(
                    'YYYY-MM-DDTHH:mm'
                  )
                : ''
            }
            type="datetime-local"
            onValueChange={(value) => {
              if (
                value &&
                futurePeriod?.initial_planned_end &&
                dayjs(value) >= dayjs(futurePeriod.initial_planned_end)
              ) {
                setStartDateError('Start date must be before end date.');
                return;
              }
              setStartDateError(null);
              updateCurrentPeriod.mutateAsync({
                incidentId: incidentData?.id || '',
                initialPlannedStart: dayjs(value).toISOString(),
                showToast: false,
              });
            }}
          />
          <PratusInput
            label="Planned End Date:"
            placeholder="Enter planned end date..."
            disabled={!canEditCards}
            value={
              futurePeriod?.initial_planned_end
                ? dayjs(futurePeriod.initial_planned_end).format(
                    'YYYY-MM-DDTHH:mm'
                  )
                : ''
            }
            type="datetime-local"
            onValueChange={(value) => {
              if (
                value &&
                futurePeriod?.initial_planned_start &&
                dayjs(value) <= dayjs(futurePeriod.initial_planned_start)
              ) {
                setEndDateError('End date must be after start date.');
                return;
              }
              setEndDateError(null);
              updateCurrentPeriod.mutateAsync({
                incidentId: incidentData?.id || '',
                initialPlannedEnd: dayjs(value).toISOString(),
                showToast: false,
              });
            }}
          />
          {(startDateError || endDateError) && (
            <span
              className="text-danger"
              style={{ fontSize: '0.85rem', whiteSpace: 'nowrap' }}
            >
              {startDateError || endDateError}
            </span>
          )}
        </div>
      </div>
      <PratusTextArea
        label="Limitations & Constraints"
        placeholder="Enter limitations and constraints..."
        disabled={!canEditCards}
        value={incidentInfo?.limitationsAndConstraints || ''}
        onSave={(value) =>
          incidentMutation.mutate({
            ...incidentInfo,
            limitationsAndConstraints: value,
          })
        }
      />
    </ConditionalCollapsibleCard>
  );
};

export default IncidentObjectives;
